import apiClient from './api';

export type DeliveryAddress = {
  id?: number;
  fullName?: string;
  phone?: string;
  area?: string;
  locality?: string;
  landmark?: string;
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
  lat?: string;
  lon?: string;
  addressType?: string;
  floor?: string;
  tower?: string;
  building?: string;
  instructions?: string;
  geocodeStatus?: string;
  placeId?: string;
  phoneCountryCode?: string;
  isDefault?: boolean;
};

export type DeliveryConfig = {
  success: boolean;
  featureEnabled?: boolean;
  enabled?: boolean;
  paused?: boolean;
  minOrderPaise?: number;
  packingPaise?: number;
  radiusM?: number;
  pricingType?: string;
};

export type DeliveryQuote = {
  success: boolean;
  quoteId?: string;
  customerChargePaise?: number;
  packingPaise?: number;
  taxPreviewPaise?: number;
  grandTotalPaise?: number;
  cartSubtotalPaise?: number;
  cgstPercent?: number;
  sgstPercent?: number;
  distanceM?: number;
  geocodeStatus?: string;
  expiresAt?: string;
  code?: string;
  message?: string;
};

export function formatDeliveryAddressLine(address?: {
  floor?: string;
  tower?: string;
  building?: string;
  area?: string;
  locality?: string;
  city?: string;
  pincode?: string;
} | null): string {
  if (!address) {
    return '';
  }
  const building = [address.floor, address.tower, address.building].filter(Boolean).join(', ');
  const street = uniqueJoin([address.area, address.locality]);
  return [building, street, address.city, address.pincode].filter(Boolean).join(', ');
}

function uniqueJoin(values: Array<string | undefined>): string {
  const seen: string[] = [];
  values.forEach((value) => {
    if (value && seen.indexOf(value) === -1) {
      seen.push(value);
    }
  });
  return seen.join(', ');
}

const deliveryService = {
  async getConfig(appName: string): Promise<DeliveryConfig> {
    const res = await apiClient.get(`/api/restaurants/${appName}/delivery/config`);
    return res.data;
  },

  async listAddresses(appName: string): Promise<{ success: boolean; addresses?: DeliveryAddress[] }> {
    const res = await apiClient.get(`/api/restaurants/${appName}/delivery/addresses`);
    return res.data;
  },

  async listUserAddresses(): Promise<{ success: boolean; addresses?: DeliveryAddress[] }> {
    const res = await apiClient.get('/api/user/delivery/addresses');
    return res.data;
  },

  async saveAddress(appName: string, address: DeliveryAddress) {
    const res = await apiClient.post(`/api/restaurants/${appName}/delivery/addresses`, address);
    return res.data as { success: boolean; address?: DeliveryAddress; message?: string };
  },

  async saveUserAddress(address: DeliveryAddress) {
    const res = await apiClient.post('/api/user/delivery/addresses', address);
    return res.data as { success: boolean; address?: DeliveryAddress; message?: string };
  },

  async deleteUserAddress(id: number) {
    const res = await apiClient.delete(`/api/user/delivery/addresses/${id}`);
    return res.data as { success: boolean; message?: string };
  },

  async setDefaultUserAddress(id: number) {
    const res = await apiClient.post(`/api/user/delivery/addresses/${id}/default`);
    return res.data as { success: boolean; address?: DeliveryAddress; message?: string };
  },

  async quote(appName: string, addressId: number) {
    const res = await apiClient.post(`/api/restaurants/${appName}/delivery/quote`, { addressId });
    return res.data as DeliveryQuote;
  },

  async serviceability(appName: string, addressId: number) {
    const res = await apiClient.post(`/api/restaurants/${appName}/delivery/serviceability`, { addressId });
    return res.data as {
      success: boolean;
      serviceable?: boolean;
      reason?: string;
      message?: string;
      code?: string;
      distanceM?: number;
    };
  },

  async tracking(appName: string, orderId: number) {
    const res = await apiClient.get(`/api/restaurants/${appName}/delivery/orders/${orderId}`);
    return res.data;
  },
};

export default deliveryService;
